import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import BoundingBoxLabelMarker from './components/BoundingBoxLabelMarker';
import { GEOJSON_PROPERTY } from './constants';
import type { Marker, UpdateMarkerParams, UseMarkers } from './types';

const useMarkers = (): UseMarkers => {
    const markerMap = useRef<Record<string, Marker>>({});
    const featureMarkerMap = useRef<Record<string, Marker>>({});
    const [, setInteractivityVersion] = useState(0);

    const addMarker = ({
        feature,
        isInteractive = false,
        position,
        rotation,
        text
    }: UpdateMarkerParams) => {
        const isStatic = feature.properties?.[GEOJSON_PROPERTY.IS_STATIC] === true;
        const isActive = feature.properties?.[GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY] === true;
        const isSelected = feature.properties?.[GEOJSON_PROPERTY.IS_SELECTED] === true;
        const newMarker: Marker = {
            featureId: feature.id as string,
            id: uuidv4(),
            isActive,
            isInteractive,
            isSelected,
            isStatic,
            markerElement: BoundingBoxLabelMarker({
                isActive,
                isInteractive,
                isSelected,
                text
            }),
            position,
            rotation,
            text
        };

        markerMap.current = {
            ...markerMap.current,
            [newMarker.id]: newMarker
        };

        featureMarkerMap.current = {
            ...featureMarkerMap.current,
            [feature.id as string]: newMarker
        };

        return newMarker;
    };

    const updateMarker = ({
        feature,
        isInteractive,
        position,
        rotation,
        text
    }: UpdateMarkerParams) => {
        const marker = featureMarkerMap.current[feature.id as string];

        if (!marker) {
            addMarker({ feature, isInteractive, position, rotation, text });

            return;
        }

        const isFeatureSelected = feature.properties?.[GEOJSON_PROPERTY.IS_SELECTED] === true;
        const isFeatureActive = feature.properties?.[GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY] === true;
        const nextIsInteractive = isInteractive ?? marker.isInteractive;

        marker.position = position;
        marker.rotation = rotation;
        marker.text = text;
        marker.isActive = isFeatureActive;
        marker.isSelected = isFeatureSelected;
        marker.isInteractive = nextIsInteractive;

        marker.markerElement = BoundingBoxLabelMarker({
            isActive: isFeatureActive,
            isInteractive: nextIsInteractive,
            isSelected: isFeatureSelected,
            text
        });
    };

    const refreshInteractivity = useCallback((isInteractive: boolean) => {
        Object.values(markerMap.current).forEach(marker => {
            marker.isInteractive = isInteractive;

            marker.markerElement = BoundingBoxLabelMarker({
                isActive: marker.isActive,
                isInteractive,
                isSelected: marker.isSelected,
                text: marker.text
            });
        });

        setInteractivityVersion(version => version + 1);
    }, []);

    const removeMarkerById = (markerId: string) => {
        const marker = markerMap.current[markerId];

        if (!marker) {
            return;
        }

        delete markerMap.current[markerId];

        const { featureId } = marker;

        delete featureMarkerMap.current[featureId];
    };

    const clear = () => {
        markerMap.current = {};
        featureMarkerMap.current = {};
    };

    const getMarkerById = (markerId: string) => markerMap.current[markerId];

    const getMarkerByFeatureId = (featureId: string) => featureMarkerMap.current[featureId];

    const removeMarkerByFeatureId = (featureId: string) => {
        const marker = featureMarkerMap.current[featureId];

        if (!marker) {
            return;
        }

        const { id } = marker;

        if (!id) {
            return;
        }

        removeMarkerById(id);
    };

    return {
        addMarker,
        clear,
        getMarkerByFeatureId,
        getMarkerById,
        markerMap: markerMap.current,
        refreshInteractivity,
        removeMarkerByFeatureId,
        removeMarkerById,
        updateMarker
    };
};

export default useMarkers;
